import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { type WhiteboardAction, type WhiteboardCommand, type WhiteboardEvent, type WhiteboardObject, type WhiteboardPreview, type WhiteboardSnapshot, type WhiteboardOpenRuns, type SavedWhiteboard, type SoloWhiteboardSnapshot, type SoloWhiteboardTarget, whiteboardBytes } from "@calega/realtime";
import { useRealtime } from "../realtime/RealtimeContext";
import { api, ApiError } from "../lib/api";

export class WhiteboardOpenRunsError extends Error {
  readonly terminal = true;
  constructor(readonly openRuns: WhiteboardOpenRuns) { super("Es gibt noch offene Übungsläufe."); }
}

export interface BoardContextValue {
  snapshot: WhiteboardSnapshot | null; ready: boolean; busy: boolean; notice: string;
  previews: WhiteboardPreview[]; saved: SavedWhiteboard[]; ending: boolean;
  command(action: WhiteboardAction, scope?: Pick<WhiteboardSnapshot, "generation" | "permissionRevision">): Promise<void>; preview(object: WhiteboardObject | null): void;
  refresh(): Promise<void>; list(): Promise<void>; save(title: string, asNew?: boolean): Promise<void>;
  load(id: string): Promise<void>; removeSaved(board: SavedWhiteboard): Promise<void>;
  requestEnd(end: () => Promise<void>): void; cancelEnd(): void; finishEnd(save: boolean, title: string): Promise<void>;
  solo?: SoloWhiteboardSnapshot; soloVisible?: boolean; closeSolo?(): void; switchSolo?(target: SoloWhiteboardTarget): void;
  personal?: SavedWhiteboard[]; listPersonal?(): Promise<void>; loadPersonal?(id: string): Promise<void>; serverNow?(): number;
}
export const BoardContext = createContext<BoardContextValue | null>(null);
export function WhiteboardProvider({ children }: { children: ReactNode }) {
  const realtime = useRealtime();
  const [snapshot, setSnapshot] = useState<WhiteboardSnapshot | null>(null);
  const current = useRef(snapshot); current.current = snapshot;
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [previews, setPreviews] = useState<WhiteboardPreview[]>([]);
  const [saved, setSaved] = useState<SavedWhiteboard[]>([]);
  const [ending, setEnding] = useState(false);
  const endRef = useRef<(() => Promise<void>) | null>(null);
  const identity = useRef(realtime.session?.id); identity.current = realtime.session?.id;
  const sequence = useRef(0), lastPreview = useRef(0);
  const pending = useRef(0);
  const pendingSave = useRef<{ signature: string; newId: string } | null>(null);
  const viewEpoch = useRef(0);
  const refreshRef = useRef<() => Promise<void>>(async () => undefined);
  const inFlight = useRef<Promise<void> | null>(null);
  const path = `/sessions/${realtime.session?.id}/whiteboard`;
  const refresh = useCallback(async () => {
    if (!realtime.session || !realtime.connected) { setReady(false); return; }
    if (inFlight.current) return inFlight.current;
    const id = realtime.session.id;
    const epoch = viewEpoch.current;
    let repeat = false;
    const promise = api<WhiteboardSnapshot>(`/sessions/${id}/whiteboard`, { cache: "no-store", signal: AbortSignal.timeout(10000) }).then((next) => {
      if (identity.current !== id) return;
      if (epoch !== viewEpoch.current) { repeat = true; return; }
      if (current.current?.generation !== next.generation) {
        if (current.current?.document.objects.length && !next.document.objects.length && !next.active) setNotice("Der temporäre Tisch ist nicht mehr verfügbar. Ein gespeichertes Whiteboard kann erneut geladen werden.");
        setPreviews([]);
      }
      if (!current.current || next.generation !== current.current.generation || next.revision >= current.current.revision) { current.current = next; setSnapshot(next); }
      setReady(true);
    }).catch((error: Error) => { if (identity.current === id) {
      setReady(false); setNotice(error.message);
      if (error instanceof ApiError && [401, 403].includes(error.status)) { current.current = null; setSnapshot(null); setPreviews([]); realtime.disconnect(); }
    } }).finally(() => { if (inFlight.current === promise) inFlight.current = null; if (repeat && identity.current === id) queueMicrotask(() => { void refreshRef.current(); }); });
    inFlight.current = promise; return promise;
  }, [realtime.session?.id, realtime.connected]);
  refreshRef.current = refresh;
  useEffect(() => {
    setSnapshot(null); current.current = null; setReady(false); setSaved([]); setPreviews([]); setNotice(""); setEnding(false); endRef.current = null; inFlight.current = null; pending.current = 0; setBusy(false);
  }, [realtime.session?.id]);
  useEffect(() => {
    const socket = realtime.whiteboardSocket;
    if (!socket || !realtime.session || !realtime.connected) { setReady(false); setPreviews([]); return; }
    let live = true;
    void refresh();
    const event = (event: WhiteboardEvent) => {
      const previous = current.current;
      if (event.sessionId !== identity.current || !live || previous && event.generation === previous.generation && event.revision <= previous.revision) return;
      viewEpoch.current++;
      setPreviews([]);
      if (!previous || event.reload || event.generation !== previous.generation || event.revision !== previous.revision + 1) { setReady(false); void refresh(); return; }
      const { changes = [], removed = [], versions = {}, reload: _reload, sessionId: _id, ...meta } = event;
      const ids = new Set([...removed, ...changes.map((entry) => entry.id)]);
      const next = { ...previous, ...meta, document: { ...previous.document, objects: [...previous.document.objects.filter((entry) => !ids.has(entry.id)), ...changes] }, versions: { ...previous.versions, ...versions } };
      current.current = next; setSnapshot(next); setReady(true);
    };
    const preview = (preview: WhiteboardPreview) => {
      const state = current.current;
      if (!live || preview.sessionId !== identity.current || !state?.active || preview.generation !== state.generation || preview.permissionRevision !== state.permissionRevision) return;
      setPreviews((current) => {
        if (current.some((entry) => entry.connectionId === preview.connectionId && entry.sequence >= preview.sequence)) return current;
        return [...current.filter((entry) => entry.connectionId !== preview.connectionId), ...(preview.object ? [preview] : [])].slice(-8);
      });
    };
    socket.on("whiteboard:event", event); socket.on("whiteboard:preview", preview);
    const check = () => { if (document.visibilityState === "visible") void refresh(); };
    const timer = window.setInterval(check, 5000);
    const previewsTimer = window.setInterval(() => setPreviews([]), 8000);
    window.addEventListener("focus", check); document.addEventListener("visibilitychange", check);
    return () => { live = false; socket.off("whiteboard:event", event); socket.off("whiteboard:preview", preview); window.clearInterval(timer); window.clearInterval(previewsTimer); window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); };
  }, [realtime.whiteboardSocket, realtime.connected, realtime.session?.id, refresh]);
  async function command(action: WhiteboardAction, scope?: Pick<WhiteboardSnapshot, "generation" | "permissionRevision">) {
    const state = current.current, socket = realtime.whiteboardSocket, id = realtime.session?.id;
    if (!state || !socket || !ready || !realtime.connected || !id) { const error = new Error("Das Whiteboard wird noch synchronisiert."); setNotice(error.message); throw error; }
    const input: WhiteboardCommand = { sessionId: id, commandId: crypto.randomUUID(), generation: scope?.generation ?? state.generation, permissionRevision: scope?.permissionRevision ?? state.permissionRevision, baseRevision: state.revision, action };
    pending.current++; setBusy(true);
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await new Promise<void>((resolve, reject) => socket.timeout(7000).emit("whiteboard:command", input, (error: Error | null, result?: { ok: boolean; error?: { message: string; status: number; code?: string; openRuns?: WhiteboardOpenRuns } }) => {
            if (identity.current !== id) return reject(new Error("Die Sitzung wurde gewechselt."));
            if (error) return reject(error);
            if (!result?.ok && result?.error?.code === "whiteboard_open_runs" && result.error.openRuns) return reject(new WhiteboardOpenRunsError(result.error.openRuns));
            if (!result?.ok) return reject(Object.assign(new Error(result?.error?.message ?? "Die Aktion konnte nicht bestätigt werden."), { terminal: true }));
            resolve();
          }));
          setNotice(""); return;
        } catch (error) { if (attempt || (error as { terminal?: boolean }).terminal || !socket.connected) throw error; }
      }
    } catch (error) { if (identity.current === id) { setNotice(error instanceof WhiteboardOpenRunsError ? "" : (error as Error).message); if (!(error instanceof WhiteboardOpenRunsError)) void refresh(); } throw error; }
    finally { if (identity.current === id) { pending.current--; setBusy(pending.current > 0); } }
  }
  async function list() {
    const id = identity.current;
    const rows = await api<SavedWhiteboard[]>(`${path}/saved`, { cache: "no-store" });
    if (identity.current === id) setSaved(rows);
  }
  async function save(title: string, asNew = false) {
    const state = current.current; if (!state) throw new Error("Kein Whiteboard verfügbar.");
    const signature = JSON.stringify([title, asNew, state.generation, state.revision]);
    if (pendingSave.current?.signature !== signature) pendingSave.current = { signature, newId: crypto.randomUUID() };
    setBusy(true);
    try {
      await api(`${path}/saved`, { method: "POST", body: JSON.stringify({ title, generation: state.generation, revision: state.revision, ...(!asNew && state.saved ? { id: state.saved.id, savedRevision: state.saved.revision } : { newId: pendingSave.current.newId }) }) });
      pendingSave.current = null;
      await refresh(); await list(); setNotice("Whiteboard gespeichert.");
    } catch (error) { setNotice((error as Error).message); throw error; } finally { setBusy(false); }
  }
  return <BoardContext.Provider value={{ snapshot, ready, busy, notice, previews, saved, ending, command, refresh, list, save,
    preview: (object) => {
      const state = current.current, socket = realtime.whiteboardSocket;
      if (!state || !socket?.connected || !ready || !realtime.session || object && whiteboardBytes(object) > 30000) return;
      if (object && Date.now() - lastPreview.current < 100) return;
      lastPreview.current = Date.now();
      socket.volatile.emit("whiteboard:preview", { sessionId: realtime.session.id, generation: state.generation, permissionRevision: state.permissionRevision, sequence: ++sequence.current, object });
    },
    load: async (id) => {
      const state = current.current; if (!state) return;
      await api(`${path}/saved/${id}/load`, { method: "POST", body: JSON.stringify({ generation: state.generation, revision: state.revision }) }); await refresh();
    },
    removeSaved: async (board) => { await api(`${path}/saved/${board.id}?revision=${board.revision}`, { method: "DELETE" }); await list(); await refresh(); },
    requestEnd: (end) => { if (current.current?.dirty && realtime.session?.participantRole === "FACILITATOR") { endRef.current = end; setEnding(true); } else void end().catch((error: Error) => setNotice(error.message)); },
    cancelEnd: () => { setEnding(false); endRef.current = null; },
    finishEnd: async (store, title) => {
      if (store) { await save(title); if (current.current?.dirty) throw new Error("Der Tisch wurde während des Speicherns verändert. Bitte erneut speichern."); }
      const end = endRef.current; if (end) await end(); setEnding(false); endRef.current = null;
    },
  }}>{children}</BoardContext.Provider>;
}
export function useWhiteboard() { const value = useContext(BoardContext); if (!value) throw new Error("WhiteboardProvider fehlt."); return value; }
